// Fetching a node's thumbnail from storage, a few at a time.
//
// Storage streams every file as application/octet-stream, so whether the
// answer is a picture is read from its first bytes (sniffImageType): only a
// picture is shown or stored, typed so the browser draws it from an object
// URL. A failed request, an error body or an empty file is "no thumbnail"
// (the card keeps its placeholder), never an error on the page.
//
// Storage answers 404 for a thumbnail row with nothing uploaded behind it
// (the row exists before its image, an upload can fail or be abandoned, or
// its files were deleted). That is a known answer, not a fault: it isn't
// written to the console, and this page doesn't ask again for that key (the
// key carries the upload time, so a new upload is a new key and is fetched).

import { getBlobAsync } from '../api.service'
import { thumbnailCache } from './thumbnail-cache'

// Cards ask for their pictures only near the viewport (use-near-viewport),
// and at most this many requests run at once, so a big folder doesn't flood
// the storage service.
export const MAX_CONCURRENT = 6

const readBytes = (blob, count) => {
  const head = blob.slice(0, count)
  if (typeof head.arrayBuffer === 'function') return head.arrayBuffer().then(buffer => new Uint8Array(buffer))
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(head)
  })
}

const startsWith = (data, signature, offset = 0) => signature.every((value, i) => value === null || data[offset + i] === value)
const ascii = text => [...text].map(c => c.charCodeAt(0))

export const sniffImageType = async blob => {
  if (!blob || !blob.size) return null
  let data
  try {
    data = await readBytes(blob, 512)
  } catch (e) {
    return null
  }
  if (startsWith(data, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (startsWith(data, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (startsWith(data, ascii('GIF8'))) return 'image/gif'
  if (startsWith(data, ascii('RIFF')) && startsWith(data, ascii('WEBP'), 8)) return 'image/webp'
  if (startsWith(data, ascii('ftypavif'), 4)) return 'image/avif'
  if (startsWith(data, ascii('BM'))) return 'image/bmp'
  const text = new TextDecoder('utf-8').decode(data).replace(/^﻿/, '').trimStart()
  if ((text.startsWith('<svg') || text.startsWith('<?xml')) && text.includes('<svg')) return 'image/svg+xml'
  return null
}

// fetchThumbnail's answer when storage says nothing has been uploaded (404).
export const NOTHING_UPLOADED = 'nothing-uploaded'

// A picture (Blob), null (no thumbnail this time), or NOTHING_UPLOADED.
export const fetchThumbnail = async assetID => {
  try {
    const response = await getBlobAsync(process.env.REACT_APP_API_STORAGE, `api/v1/file-content/${assetID}/stream`, { quietNotFound: true })
    const blob = response && response.data
    if (!(blob instanceof Blob)) return null
    const type = await sniffImageType(blob)
    if (!type) return null
    return blob.type === type ? blob : blob.slice(0, blob.size, type)
  } catch (e) {
    return e && e.status === 404 ? NOTHING_UPLOADED : null
  }
}

// Keys storage answered 404 for, this page only; past the cap the oldest go.
export const MAX_REMEMBERED_MISSES = 1000

export const createMissSet = (max = MAX_REMEMBERED_MISSES) => {
  const keys = new Set()
  return {
    has: key => keys.has(key),
    add: key => {
      keys.delete(key)
      keys.add(key)
      while (keys.size > max) keys.delete(keys.values().next().value)
    },
    clear: () => keys.clear()
  }
}

export const thumbnailMisses = createMissSet()

const abortError = () => {
  const error = new Error('The thumbnail was no longer wanted.')
  error.name = 'AbortError'
  return error
}

// limit(task, signal): runs task when one of `max` places is free, in the
// order asked. A task whose signal is aborted while it waits never runs.
export const createLimiter = max => {
  let running = 0
  const queue = []

  const done = () => {
    running -= 1
    next()
  }

  const run = job => {
    running += 1
    Promise.resolve()
      .then(job.task)
      .then(job.resolve, job.reject)
      .finally(done)
  }

  const next = () => {
    while (running < max && queue.length) {
      const job = queue.shift()
      if (job.signal && job.signal.aborted) job.reject(abortError())
      else run(job)
    }
  }

  return (task, signal) =>
    new Promise((resolve, reject) => {
      if (signal && signal.aborted) {
        reject(abortError())
        return
      }
      const job = { task, signal, resolve, reject }
      queue.push(job)
      if (signal) {
        signal.addEventListener('abort', () => {
          const at = queue.indexOf(job)
          if (at >= 0) {
            queue.splice(at, 1)
            reject(abortError())
          }
        })
      }
      next()
    })
}

const limiter = createLimiter(MAX_CONCURRENT)
const inflight = new Map()

// One thumbnail from the network, remembered (memory and browser) when it is
// a picture. A card that gives up while it waits for its turn (signal) costs
// nothing; once a request has started it is shared by every card asking for
// that key, and one already remembered when its turn comes costs none.
export const loadThumbnail = (asset, key, { signal, cache = thumbnailCache, fetch = fetchThumbnail, limit = limiter, misses = thumbnailMisses } = {}) => {
  if (key && misses.has(key)) return Promise.resolve(null)
  if (key && inflight.has(key)) return inflight.get(key)

  return limit(() => {
    const hit = key && cache.peek(key)
    if (hit) return hit
    if (key && inflight.has(key)) return inflight.get(key)
    const fetching = Promise.resolve(fetch(asset.id)).then(found => {
      if (found === NOTHING_UPLOADED) {
        if (key) misses.add(key)
        return null
      }
      if (found && key) cache.put(key, found)
      return found
    })
    if (key) {
      inflight.set(key, fetching)
      fetching
        .finally(() => {
          if (inflight.get(key) === fetching) inflight.delete(key)
        })
        .catch(() => {})
    }
    return fetching
  }, signal)
}
